import { useQuery } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray } from '@/types/shared';

/**
 * Customer SLA & Tickets tab — `GET /customers/:id/tickets`. Field names are the
 * v4 backend verbatim (verified live). Server-paginated. Powers the customer's
 * support-ticket history + SLA view on the detail page.
 */

export type TicketStatus =
  | 'opening'
  | 'work-in-progress'
  | 'pending'
  | 'cancelled'
  | 'closed'
  | 'completed';

export type TicketPriority = 'high' | 'medium' | 'low';

/** `GET /customers/:id/tickets` row. */
export interface CustomerTicketRow {
  ticket_id: number;
  ticket_status: TicketStatus | string;
  ticket_priority: TicketPriority | string;
  ticket_content?: string;
  ticket_requested_at?: string;
  ticket_time_release?: string | null;
  ticket_estimated_completion_date?: string | null;
  ticket_marjorable?: boolean;
  ticket_source?: string;
  ticket_product_name?: string;
  ticket_type_title?: string;
  ticket_technical_assignees?: unknown;
  created_at?: string;
  service_id?: number;
  service_name?: string;
  product_group_name?: string;
  employee_id?: number;
  employee_name?: string;
  avg_rating?: number;
  feedback_count?: number;
}

export interface CustomerTicketsParams {
  page?: number;
  page_size?: number;
  q?: string;
}

/** `GET /customers/:id/tickets` — support tickets for one customer. */
export function useCustomerTickets(customerId: number, params: CustomerTicketsParams = {}) {
  return useQuery({
    queryKey: ['customers', customerId, 'tickets', params],
    enabled: !!customerId,
    queryFn: async (): Promise<ResponseArray<CustomerTicketRow>> => {
      const res = await dataCentralApi.get<ResponseArray<CustomerTicketRow>>(
        `/customers/${customerId}/tickets?${qs.stringify({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}
